/*
  The product page's gallery arrows, which only exist when scripting does. The page is
  correct and complete before this file runs; nothing here is load-bearing.
*/
(() => {
  /*
    The comp puts a prev and a next arrow over the bottom right of the photograph. Dawn has
    a pair, and hides them above 750px, because above that width its gallery is not a
    scroller: one media is shown and the thumbnails switch it. So rather than trying to
    revive a slider that is not running, these two drive the thumbnails, which is what a
    shopper clicking an arrow means. Injected rather than written into the markup so that
    a gallery with one photograph never gets a control that cannot do anything.
  */
  const galleryArrows = (root) => {
    root.querySelectorAll('media-gallery[data-desktop-layout^="thumbnail"]').forEach((gallery) => {
      if (gallery.dataset.atlasArrows) return;
      const viewer = gallery.querySelector('slider-component');
      const thumbs = [...gallery.querySelectorAll('.thumbnail-list__item .thumbnail')];
      if (!viewer || thumbs.length < 2) return;
      gallery.dataset.atlasArrows = 'true';

      const step = (delta) => {
        const at = thumbs.findIndex((t) => t.getAttribute('aria-current') === 'true');
        const next = thumbs[((at < 0 ? 0 : at) + delta + thumbs.length) % thumbs.length];
        if (next) next.click();
      };

      const nav = document.createElement('div');
      nav.className = 'atlas-galnav';
      [['previous', -1, 'M15 19.5 7.5 12l7.5-7.5'], ['next', 1, 'm8.25 4.5 7.5 7.5-7.5 7.5']].forEach(
        ([label, delta, path]) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'atlas-galnav__button';
          button.setAttribute('aria-label', label === 'previous' ? 'Previous image' : 'Next image');
          button.innerHTML =
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" ' +
            'aria-hidden="true" focusable="false"><path stroke-linecap="round" ' +
            'stroke-linejoin="round" d="' + path + '"/></svg>';
          button.addEventListener('click', () => step(delta));
          nav.appendChild(button);
        }
      );
      viewer.appendChild(nav);

      /*
        A mouse resting on a thumbnail shows that photograph (the tracker of 2026-10-18:
        "Hovering the thumbnail on the slider should switch to the image"). After a short
        pause, so sweeping across the row on the way somewhere else does not flick through
        every one; touch and keyboard still choose by tapping or Enter, as before.
      */
      let hoverTimer = 0;
      thumbs.forEach((thumb) => {
        thumb.addEventListener('pointerenter', (event) => {
          if (event.pointerType !== 'mouse') return;
          clearTimeout(hoverTimer);
          hoverTimer = setTimeout(() => {
            if (thumb.getAttribute('aria-current') !== 'true') thumb.click();
          }, 90);
        });
        thumb.addEventListener('pointerleave', () => clearTimeout(hoverTimer));
      });
    });
  };

  /*
    "See it in use" over the first photograph. A pointer resting on the photograph shows
    the in-use shot through CSS alone; this button is the way to it on a phone, and for
    a keyboard. It flips a class on the photograph's own container, so the shot sits in
    the same box and nothing around it moves.
  */
  const inUseToggles = (root) => {
    root.querySelectorAll('[data-atlas-inuse-toggle]').forEach((button) => {
      if (button.dataset.atlasWired) return;
      button.dataset.atlasWired = 'true';
      const box = button.closest('.product-media-container');
      if (!box) return;
      button.addEventListener('click', () => {
        const on = !box.classList.contains('is-inuse');
        box.classList.toggle('is-inuse', on);
        button.setAttribute('aria-pressed', on ? 'true' : 'false');
        button.querySelector('span:last-child').textContent = on ? 'Back to the photo' : 'See it in use';
      });
    });
  };

  /*
    The in-use shot is written without a source (snippets/product-thumbnail.liquid says why:
    it was racing the photograph it lies over). It gets one when the page has loaded and the
    browser is idle, so it is there before anyone hovers, or at once on the first pointer or
    tap on the photograph if that comes sooner.
  */
  const feedInUse = (root) => {
    root.querySelectorAll('img.atlas-inuse[data-atlas-inuse-src]').forEach((img) => {
      if (img.dataset.atlasFed) return;
      img.dataset.atlasFed = 'true';
      const give = () => {
        if (img.getAttribute('src')) return;
        img.srcset = img.dataset.atlasInuseSrcset;
        img.src = img.dataset.atlasInuseSrc;
      };
      const box = img.closest('.product-media-container') || img.parentElement;
      box.addEventListener('pointerenter', give, { once: true });
      box.addEventListener('touchstart', give, { once: true, passive: true });
      const toggle = box.querySelector('[data-atlas-inuse-toggle]');
      if (toggle) toggle.addEventListener('click', give, { once: true });
      const later = () => (window.requestIdleCallback || ((fn) => setTimeout(fn, 1500)))(give);
      if (document.readyState === 'complete') later();
      else window.addEventListener('load', later, { once: true });
    });
  };

  /*
    "Read more" at the end of the opening paragraph (Shawna's comp of 2026-10-16) opens
    Details, under the buttons, and brings it into view. Without scripting it is an anchor
    to the same place.
  */
  const readMore = (root) => {
    root.querySelectorAll('[data-atlas-readmore]').forEach((link) => {
      if (link.dataset.atlasWired) return;
      link.dataset.atlasWired = 'true';
      link.addEventListener('click', (event) => {
        const box = document.querySelector(link.getAttribute('href'));
        if (!box) return;
        event.preventDefault();
        const details = box.querySelector('details');
        if (details) details.open = true;
        box.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  };

  /*
    The size row shows five sizes and "More" (the comp: 2x3, 2.5x4, 3x5, 4x6, 5x8, More).
    The size being looked at is always among the five. "More" shows the rest in place and
    becomes "Fewer". Without scripting every size is on the row, as before.
  */
  const SIZES_SHOWN = 5;
  const sizeMore = (root) => {
    root.querySelectorAll('[data-atlas-sizefam]').forEach((family) => {
      const list = family.querySelector('.atlas-sizefam__list');
      if (!list || list.dataset.atlasMore) return;
      const items = [...list.querySelectorAll(':scope > .atlas-sizefam__item')];
      if (items.length <= SIZES_SHOWN + 1) return;
      list.dataset.atlasMore = 'true';
      const current = items.findIndex((li) => li.querySelector('.atlas-sizetile--current'));
      const shown = new Set(items.slice(0, SIZES_SHOWN).map((li, i) => i));
      if (current >= SIZES_SHOWN) {
        shown.delete(SIZES_SHOWN - 1);
        shown.add(current);
      }
      items.forEach((li, i) => { if (!shown.has(i)) li.classList.add('atlas-sizefam__item--extra'); });
      const more = document.createElement('li');
      more.className = 'atlas-sizefam__item atlas-sizefam__item--more';
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'atlas-sizefam__more';
      button.setAttribute('aria-expanded', 'false');
      button.textContent = 'More';
      button.addEventListener('click', () => {
        const open = !list.classList.contains('is-open');
        list.classList.toggle('is-open', open);
        button.setAttribute('aria-expanded', String(open));
        button.textContent = open ? 'Fewer' : 'More';
      });
      more.appendChild(button);
      list.appendChild(more);
      list.classList.add('is-collapsible');
    });
  };

  const wire = (root) => {
    galleryArrows(root);
    inUseToggles(root);
    feedInUse(root);
    readMore(root);
    sizeMore(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => wire(document));
  } else {
    wire(document);
  }

  // A variant change re-renders the buy column from the server, which replaces both of
  // these elements with fresh markup in its original, uncollapsed state.
  document.addEventListener('variant:change', () => wire(document));
  document.addEventListener('product-info:loaded', () => wire(document));
})();
